// React
import { useEffect, useState } from "react";

// Material-UI
import { Box, Paper, Skeleton, Stack, SvgIcon, TextField } from "@mui/material";
import clsx from "clsx";

// ICONS
import { ReactComponent as InformationIcon } from "@/assets/icons/common/circled_information.svg";

interface TitleEditProps {
	defaultTitle?: string;
	title?: string;
	classes?: string;
	loading?: boolean;
	readOnly?: boolean;
	noEditable?: boolean;
	onChange?: (value: string) => void;
	onEditComplete?: (value: string) => void;
	minRows?: number;
	maxRows?: number;
	rows?: number;
	sx?: object;
	maxLength?: number;
}

interface WarningDefaultTitleProps {
	isDefaultTitle: boolean;
}

function WarningDefaultTitle(props: Readonly<WarningDefaultTitleProps>) {
	if (props.isDefaultTitle) {
		return (
			<Box className="absolute bottom-[-20px] flex">
				<SvgIcon className="!text-alert-color !text-[16px] !mt-[5px]" component={InformationIcon}></SvgIcon>
				<label className="font-sans text-[14px] text-alert-color">Must edit default setup name.</label>
			</Box>
		);
	}

	return null;
}

export default function TitleEdit(props: Readonly<TitleEditProps>) {
	const [edit, setEdit] = useState(false);
	const [noEditable, setNoEditable] = useState(false);
	const [title, setTitle] = useState("");
	const [originalTitle, setOriginalTitle] = useState("");
	const [classes, setClasses] = useState("");
	const [error, setError] = useState(false);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (typeof value !== "undefined") {
			if (edit && !noEditable) {
				setTitle(value);
				// Create a regex to check if the value is alphanumeric
				const regex = /^[a-zA-Z0-9].*$/;
				setError(!regex.test(value));
			}
			if (props.onChange && !noEditable) {
				props.onChange(value);
			}
		}
	};

	const onClickLeave = (titleValue = "") => {
		if (titleValue.trim() !== props.title && edit && props.onEditComplete && !error) {
			props.onEditComplete(titleValue);
			setEdit(false);
		}

		if (titleValue.trim() === props.title) {
			setTitle(props.title);
			setEdit(false);
		}
	};

	const handleKeyboardEvents = (e: React.KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case "Enter":
				if (edit) {
					const target = e.target as HTMLInputElement;
					setTitle(target.value);
					setOriginalTitle(target.value);
					onClickLeave(target.value);
				}
				break;
			case "Escape":
				setTitle(originalTitle);
				onClickLeave(originalTitle);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		if (props.title) {
			setTitle(props.title);
			setOriginalTitle(props.title);
		}
		if (props.classes) {
			setClasses(props.classes);
		}
		if (typeof props.noEditable !== "undefined") {
			setNoEditable(props.noEditable);
		}

		return () => {
			setTitle("");
			setOriginalTitle("");
			setNoEditable(false);
			setEdit(false);
			setError(false);
		};
	}, [props]);

	function isDefaultTitle() {
		return title === props?.defaultTitle;
	}

	return (
		<Paper elevation={0} className="!bg-transparent !relative">
			{props.loading ? (
				<Skeleton
					variant="rectangular"
					height={props.classes?.includes("section-description") ? 25 : 36}
					width={props.classes?.includes("section-description") ? 280 : 548}
					sx={{ borderRadius: "6px" }}
				></Skeleton>
			) : (
				<Stack>
					<TextField
						InputProps={{
							readOnly: props.readOnly,
						}}
						inputProps={{
							maxLength: props.maxLength,
						}}
						fullWidth
						multiline
						minRows={props.minRows}
						maxRows={props.maxRows}
						rows={props.rows}
						variant="outlined"
						autoComplete={"off"}
						spellCheck={"false"}
						className={clsx({
							[classes]: true,
							"general-section-title": true,
							"general-section-title-error": error || isDefaultTitle(),
							"[&_.MuiOutlinedInput-notchedOutline]:rounded-[6px] [&_.MuiOutlinedInput-notchedOutline]:!border-primary": edit && !noEditable,
							"[&_.MuiInputBase-input]:cursor-auto [&_.MuiInputBase-input]:caret-inherit [&_.MuiInputBase-input]:!bg-white": edit && !noEditable,
							"general-section-title-no-editable": noEditable,
							"[&>.MuiInputBase-root>textarea]:!text-alert-color": isDefaultTitle(),
							"[&>.MuiInputBase-root>textarea]:!bg-red-color-5percent": isDefaultTitle(),
							"[&>.MuiInputBase-root.Mui-focused>textarea]:!border-black": isDefaultTitle(),
						})}
						value={title}
						onChange={handleChange}
						onClick={() => {
							setEdit(true);
						}}
						onBlur={() => onClickLeave(title)}
						onKeyDown={handleKeyboardEvents}
						sx={props.sx}
					/>
					<WarningDefaultTitle isDefaultTitle={isDefaultTitle()} />
				</Stack>
			)}
		</Paper>
	);
}
